import { useCallback } from 'react';
import { useLocalStorage } from '~/hooks/useLocalStorage';

/**
 * Interface for storing last deployment information in session storage
 */
export interface LastDeploymentInfo {
  siteId: string;
  siteName: string;
  spaceName: string;
  /** Timestamp when this info was saved for staleness detection */
  savedAt: number;
}

/**
 * Storage key for last deployment info in session storage
 */
const STORAGE_KEY = 'deployment.lastUsed';

/**
 * Maximum age for deployment info before considering it stale (24 hours)
 */
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

/**
 * Hook for managing deployment storage operations
 */
export function useDeploymentStorage() {
  const storage = useLocalStorage({ session: true });

  /**
   * Clear deployment information from session storage
   */
  const clear = useCallback(() => {
    try {
      storage.set(STORAGE_KEY, '');
      if (process.env.NODE_ENV === 'development') {
        console.log('🧹 Cleared deployment info from session storage');
      }
    } catch (error) {
      console.error(
        'Failed to clear deployment info from session storage:',
        error
      );
    }
  }, [storage]);

  /**
   * Save deployment information to session storage
   */
  const save = useCallback(
    (info: Omit<LastDeploymentInfo, 'savedAt'>) => {
      try {
        const deploymentInfo: LastDeploymentInfo = {
          ...info,
          savedAt: Date.now(),
        };

        storage.set(STORAGE_KEY, JSON.stringify(deploymentInfo));
      } catch (error) {
        console.error(
          'Failed to save deployment info to session storage:',
          error
        );
      }
    },
    [storage]
  );

  /**
   * Retrieve deployment information from session storage
   * Returns null if not found, expired, or invalid
   */
  const get = useCallback((): LastDeploymentInfo | null => {
    try {
      const stored = storage.get(STORAGE_KEY);
      if (!stored) {
        return null;
      }

      const parsed = JSON.parse(stored) as LastDeploymentInfo;

      // Validate required fields
      if (
        !parsed.siteId ||
        !parsed.siteName ||
        !parsed.spaceName ||
        !parsed.savedAt
      ) {
        console.warn('Invalid deployment info in session storage, clearing');
        clear();
        return null;
      }

      // Check if data is too old (stale)
      const age = Date.now() - parsed.savedAt;
      if (age > MAX_AGE_MS) {
        clear();
        return null;
      }

      return parsed;
    } catch (error) {
      console.error(
        'Failed to retrieve deployment info from session storage:',
        error
      );
      // Clear corrupted data
      clear();
      return null;
    }
  }, [storage, clear]);

  /**
   * Check if we have valid deployment info without retrieving it
   */
  const hasValidInfo = useCallback((): boolean => {
    return get() !== null;
  }, [get]);

  return { save, get, clear, hasValidInfo };
}

/**
 * Utility function to create a Site object from stored deployment info
 * This creates the minimal Site structure needed for form fields
 */
export function createSiteFromDeploymentInfo(info: LastDeploymentInfo) {
  return {
    id: info.siteId,
    name: info.siteName,
    // Add other minimal required fields if needed by the form
    __typename: 'Site' as const,
  };
}
